import _ from 'lodash';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import Quill from 'quill/core';
import type { Delta } from 'quill/core';
import AvatarStack from '../components/AvatarStack';
import RightPanel from '../components/RightPanel';
import Timestamp from '../components/Timestamp';
import UserRow from '../user/UserRow';
import styles from './PostInsights.module.scss';
import PostInsightsBacklinks from './PostInsightsBacklinks';
import useAppSelector from '../app/redux/useAppSelector';
import type { TableDocument } from '../editor/table/types';
import addTestId from '../utils/addTestId';
import { gql } from '../graphql/generated/gql';

const CJK_LETTER_REGEX =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/gu;
const PUNCTUATION_REGEX = /\p{Punctuation}/gu;
const LETTER_REGEX = /[\p{L}\d]/gu;

export const calculateWordCount = (text: string) => {
  let nonPunctuationCJKLetterCount = 0;

  const nonCJKText = text.replace(CJK_LETTER_REGEX, (match) => {
    if (!match.match(PUNCTUATION_REGEX)) {
      nonPunctuationCJKLetterCount += 1;
    }
    return ' ';
  });

  const nonCJKWordCount = nonCJKText
    .split(/\s+/)
    .filter((part) => part.match(LETTER_REGEX)).length;

  return nonCJKWordCount + nonPunctuationCJKLetterCount;
};

export const calculateCharacterCount = (text: string) =>
  [...text.replace(/\n/g, '')].length;

const processDelta = (
  ops: Delta['ops'],
): { text: string; embedCount: number } => {
  let embedCount = 0;
  let text = '';

  ops.forEach(({ insert }) => {
    if (typeof insert === 'string') {
      text += insert;
    } else if (typeof insert === 'object' && insert['code-embed']) {
      const processed = processDelta(insert['code-embed'] as Delta['ops']);
      text += processed.text;
      embedCount += processed.embedCount;
    } else if (typeof insert === 'object' && insert['table-embed']) {
      const { cells = {} } = insert['table-embed'] as TableDocument;

      Object.values(cells).forEach(({ content }) => {
        if (content) {
          const processed = processDelta(content);
          text += processed.text;
          embedCount += processed.embedCount;
        }
      });
    } else {
      embedCount += 1;
      text += ' ';
    }
  });

  return { text, embedCount };
};

const PostInsightsMetrics = lazy(
  () =>
    import(
      /* webpackChunkName: "postMetrics" */
      './PostInsightsMetrics'
    ),
);

const POST_INSIGHTS_QUERY = gql(`
  query postInsights($id: ID!) {
    post(id: $id) {
      id
      owner {
        id
        ...UserRow_user
      }
      contributors {
        id
        ...AvatarStack_users
      }
      viewers {
        id
        ...AvatarStack_users
      }
      insertedAt
      updatedAt
      publishedAt
      ...PostInsightsBacklinks_post
    }
  }
`);

interface PostInsightsProps {
  close: () => void;
  id: string;
}

interface PostCounts {
  word: number | null;
  character: number | null;
}

const usePostCounts = () => {
  const quill = useAppSelector((state) => state.editor.quill);
  const [postCounts, setPostCounts] = useState<PostCounts>({
    word: null,
    character: null,
  });

  useEffect(() => {
    if (!quill) return undefined;

    const updatePostCounts = (delta: Delta) => {
      const { text, embedCount } = processDelta(delta.ops);
      setPostCounts({
        word: calculateWordCount(text) + embedCount,
        character: calculateCharacterCount(text),
      });
    };

    updatePostCounts(quill.getContents());

    const textChangeHandler = _.debounce(
      (change: Delta, oldDelta: Delta) =>
        updatePostCounts(oldDelta.compose(change)),
      1000,
      { maxWait: 3000 },
    );
    quill.on(Quill.events.TEXT_CHANGE, textChangeHandler);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, textChangeHandler);
      textChangeHandler.cancel();
    };
  }, [quill]);

  return postCounts;
};

const PostInsights = ({ close, id }: PostInsightsProps) => {
  const postCounts = usePostCounts();

  const { data } = useQuery(POST_INSIGHTS_QUERY, { variables: { id } });

  return (
    <RightPanel header="Post insights" close={close}>
      {data ? (
        <>
          <Suspense fallback={null}>
            <PostInsightsMetrics id={id} />
          </Suspense>
          <div className={styles.ownerSection}>
            <div className={styles.sectionHeader}>owner</div>
            <UserRow link user={data.post.owner} avatarSize={32} />
          </div>
          <div className={styles.contributorsSection}>
            <div className={styles.sectionHeader}>contributors</div>
            <AvatarStack
              users={data.post.contributors}
              limit={5}
              link
              expandable
            />
          </div>
          <div className={styles.readersSection}>
            <div className={styles.sectionHeader}>readers</div>
            <AvatarStack
              users={data.post.viewers}
              limit={5}
              link
              tooltip="View readers"
              expandable
            />
          </div>
          <div className={styles.sectionGroup}>
            <div className={styles.section}>
              {data.post.publishedAt ? (
                <>
                  <div className={styles.sectionGroupHeader}>
                    date published
                  </div>
                  {moment(data.post.publishedAt).format('MMM D, YYYY')}
                </>
              ) : (
                <>
                  <div className={styles.sectionGroupHeader}>date created</div>
                  {moment(data.post.insertedAt).format('MMM D, YYYY')}
                </>
              )}
            </div>
            <div className={styles.section}>
              <div className={styles.sectionGroupHeader}>last updated</div>
              <Timestamp date={data.post.updatedAt} />
            </div>
          </div>
          <div className={styles.sectionGroup}>
            <div className={styles.section}>
              <div className={styles.sectionGroupHeader}>word count</div>
              <span {...addTestId('postInsightsWordCount')}>
                {postCounts.word?.toLocaleString()}
              </span>
            </div>
            <div className={styles.section}>
              <div className={styles.sectionGroupHeader}>character count</div>
              <span {...addTestId('postInsightsCharacterCount')}>
                {postCounts.character?.toLocaleString()}
              </span>
            </div>
          </div>
          {data.post.backlinks.length > 0 && (
            <div className={styles.backlinksSection}>
              <div className={styles.sectionHeader}>backlinks</div>
              <PostInsightsBacklinks post={data.post} showHoverCard />
            </div>
          )}
        </>
      ) : undefined}
    </RightPanel>
  );
};

export default PostInsights;
